/* eslint-disable react/button-has-type -- it's oke */
import {
  forwardRef,
  Suspense,
  lazy,
  type ReactNode,
  type ButtonHTMLAttributes
} from "react";
import { type IconSvgElement } from "@hugeicons/react";
import { cx } from "../../utils/classnames";
import type { UntitledIcon } from "../../types/untitled-icon";
import type { IconSize } from "../IconBridge";
import { IconBridge } from "../IconBridge";

// Lazy load Tooltip components
const Tooltip = lazy(() =>
  import("../Tooltip").then((module) => ({ default: module.Tooltip }))
);
const TooltipContent = lazy(() =>
  import("../Tooltip").then((module) => ({ default: module.TooltipContent }))
);
const TooltipTrigger = lazy(() =>
  import("../Tooltip").then((module) => ({ default: module.TooltipTrigger }))
);

export enum IconButtonVariant {
  PRIMARY = "PRIMARY",
  BASE = "BASE",
  TEXT = "TEXT",
  TEXT_BASE = "TEXT_BASE"
}

export enum IconButtonSize {
  XS = "XS",
  SM = "SM",
  BASE = "BASE",
  LG = "LG"
}

interface ButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  Icon?: UntitledIcon;
  HugeIcon?: IconSvgElement;
  type?: "submit" | "reset" | "button";
  loading?: boolean;
  borderLess?: boolean;
  disabled?: boolean;
  strokeWidth?: number;
  tooltip?: string | ReactNode;
}

const buttonVariant = {
  [IconButtonVariant.PRIMARY]:
    "bg-primary-500 text-white stroke-white border-transparent text-white hover:bg-primary-600",
  // TODO: Most probably we will not need this variant
  // [ButtonVariant.DANGER]: "bg-content-1000-inverted border text-danger-500 border-danger-500 hover:bg-danger-50",
  [IconButtonVariant.BASE]:
    "bg-content-1000-inverted text-content-1000 stroke-content-1000 border-alpha-100 hover:bg-alpha-40 disabled:hover:bg-content-1000-inverted disabled:stroke-alpha-100 disabled:cursor-not-allowed disabled:text-alpha-100",
  [IconButtonVariant.TEXT]:
    "text-primary-500 stroke-primary-500 hover:text-primary-600 hover:stroke-primary-600",
  [IconButtonVariant.TEXT_BASE]:
    "text-content-500 stroke-content-500 hover:text-content-600 hover:stroke-content-600 disabled:text-alpha-100 disabled:hover:bg-text-alpha-100 disabled:stroke-alpha-100 disabled:cursor-not-allowed"
};

const buttonSize = (isInline: boolean) => {
  return {
    [IconButtonSize.XS]: cx(
      "text-xs",
      !isInline ? "min-h-[34px] h-[34px] max-h-[34px] w-[34px] rounded-lg" : ""
    ),
    [IconButtonSize.SM]: cx(
      "text-sm",
      !isInline ? "min-h-[40px] h-[40px] max-h-[40px] w-[40px] rounded-lg" : ""
    ),
    [IconButtonSize.BASE]: cx(
      "text-sm",
      !isInline ? "min-h-[48px] h-[48px] max-h-[48px] w-[48px] rounded-lg" : ""
    ),
    [IconButtonSize.LG]: cx(
      "text-base",
      !isInline ? "min-h-[56px] h-[56px] max-h-[56px] w-[56px] rounded-lg" : ""
    )
  };
};

export const IconButtonComp = (
  {
    className,
    loading,
    Icon,
    variant = IconButtonVariant.BASE,
    size = IconButtonSize.BASE,
    borderLess,
    type,
    HugeIcon,
    disabled,
    strokeWidth,
    tooltip,
    ...rest
  }: ButtonProps,
  ref?: React.Ref<HTMLButtonElement>
) => {
  if (
    process.env.NODE_ENV !== "production" &&
    borderLess &&
    variant !== IconButtonVariant.BASE
  ) {
    throw new Error("Button with prop borderLess must have variant BASE");
  }

  const isInline = Boolean(
    [IconButtonVariant.TEXT, IconButtonVariant.TEXT_BASE].includes(variant) ||
    className?.includes("inline")
  );

  const baseClass = (isInline ? "inline" : "border-box").concat(
    " ",
    "stroke-2 font-bold transition-all outline-inherit focus:outline-1 focus:outline-primary-500"
  );

  const buttonElement = (
    <button
      className={cx(
        baseClass,
        buttonVariant[variant],
        buttonSize(isInline)[size],
        borderLess ||
          [IconButtonVariant.TEXT, IconButtonVariant.TEXT_BASE].includes(
            variant
          )
          ? "border-[none]"
          : "border shadow-xs",
        className
      )}
      disabled={disabled || loading}
      ref={ref}
      type={type || "button"}
      {...rest}
    >
      <IconBridge
        size={size as unknown as IconSize}
        Icon={Icon}
        HugeIcon={HugeIcon}
        className="mx-auto"
        strokeWidth={strokeWidth}
      />
    </button>
  );

  if (tooltip) {
    return (
      <Suspense fallback={null}>
        <Tooltip>
          <TooltipTrigger className={className} asChild>
            {buttonElement}
          </TooltipTrigger>
          <TooltipContent className="bg-alpha-800 rounded-lg border p-4 text-sm text-white">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </Suspense>
    );
  }

  return buttonElement;
};

const IconButton = forwardRef(IconButtonComp);
IconButton.displayName;

export { IconButton };
